import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { ArrowLeft, FileText, Book } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import EmbeddedLecture from '@/components/lectures/EmbeddedLecture';
import { apiRequest } from '@/lib/queryClient';

const TestosteroneEmbedExample = () => {
  const [, navigate] = useLocation();
  const [embedUrl, setEmbedUrl] = useState<string>('');
  const [lectureMetadata, setLectureMetadata] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch the embed URL for this lecture
    const fetchEmbedUrl = async () => {
      try {
        const response = await apiRequest('GET', '/api/lectures/testosterone-molecular-foundations-embed/embed-url');
        const data = await response.json();
        
        if (data.embedUrl) {
          setEmbedUrl(data.embedUrl);
          setLectureMetadata(data);
        }
      } catch (error) {
        console.error('Failed to fetch lecture embed URL:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmbedUrl();
  }, []);

  const handleSlideView = (slideId: string, slideIndex: string) => {
    // Track analytics or update progress
    console.log(`Viewed slide ${slideIndex} (ID: ${slideId})`);
  };

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              className="flex items-center text-blue-600"
              onClick={() => navigate("/mentorship")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
          </div>
          
          <div>
            {/* Breadcrumb Navigation */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-600">Testosterone: Molecular Foundations (Embedded)</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
              Testosterone: Molecular Foundations
            </h1>
            <p className="text-[#7F8C8D] mb-6">
              Understanding the molecular mechanisms of testosterone and its clinical applications in longevity medicine.
            </p>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <FileText className="h-4 w-4 mr-1" />
                <span>Lecture Duration: 45 minutes</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-gray-300"></div>
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <Book className="h-4 w-4 mr-1" />
                <span>
                  {lectureMetadata?.lastUpdated 
                    ? `Last Updated: ${new Date(lectureMetadata.lastUpdated).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}`
                    : 'Loading...'
                  }
                </span>
              </div>
              {lectureMetadata?.version && (
                <>
                  <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                  <div className="text-sm text-[#7F8C8D]">
                    Version: {lectureMetadata.version}
                  </div>
                </>
              )}
            </div>
            
            <Separator />
            
            {/* Embedded Lecture Content */}
            <div className="mt-8">
              {isLoading ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Loading lecture content...</p>
                </div>
              ) : embedUrl ? (
                <EmbeddedLecture
                  embedUrl={embedUrl}
                  lectureId="testosterone-molecular-foundations-embed"
                  title="Testosterone: Molecular Foundations"
                  onSlideView={handleSlideView}
                />
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No embedded content available for this lecture yet.</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Use the lecture generator to create HTML content and upload it.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default TestosteroneEmbedExample; 